// ── WorkflowsPage ──────────────────────────────────────────────────
//
// Settings UI for the unified follow-up automation engine. Lists
// workflows and provides a linear step builder (Phase 1):
//   trigger → ordered steps (Send WhatsApp / Send Email / Notify / Wait),
//   each with a recipient (Client / Counselor / Admin) + delay.

import { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, GripVertical, Zap, Mail, MessageCircle, Bell, Clock, Activity, X } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { toast } from '@/components/ui/toast';
import { ConfirmModal } from '@/components/ConfirmModal';
import { WorkflowCanvasBuilder } from '../components/WorkflowCanvasBuilder';
import {
  listWorkflows,
  createWorkflow,
  updateWorkflow,
  toggleWorkflow,
  deleteWorkflow,
  listWhatsappTemplateOptions,
  listEmailTemplateOptions,
  getWorkflowRuns,
  type Workflow,
  type WorkflowStep,
  type TriggerType,
  type StepType,
  type RecipientType,
  type TemplateOption,
  type WorkflowRunRow,
} from '../api/workflow.api';
import { TimeDropdown } from '@/components/TimeDropdown';
import { SelectField } from '@/components/ui/SelectField';

const TRIGGER_LABELS: Record<TriggerType, string> = {
  LEAD_CREATED: 'New lead created',
  LEAD_ASSIGNED: 'Lead assigned to counselor',
  LEAD_STATUS_CHANGED: 'Lead status changed',
  MEETING_BOOKED: 'Meeting booked',
  FOLLOWUP_DUE: 'Follow-up due',
  MEETING_UPCOMING: 'Meeting starting soon',
};

// Canvas (v2) workflows keep their steps in `definition`; old ones in `steps`.
const stepCount = (wf: { steps?: unknown[]; definition?: any }) =>
  Array.isArray(wf.definition?.steps) && wf.definition.steps.length > 0 ? wf.definition.steps.length : (wf.steps?.length ?? 0);

const STEP_META: Record<StepType, { label: string; icon: React.ElementType }> = {
  SEND_WHATSAPP: { label: 'Send WhatsApp', icon: MessageCircle },
  SEND_EMAIL: { label: 'Send Email', icon: Mail },
  NOTIFY: { label: 'Notify (in-app)', icon: Bell },
  WAIT: { label: 'Wait / delay', icon: Clock },
  SEND_SMS: { label: 'Send SMS', icon: MessageCircle },
};

const RECIPIENT_LABELS: Record<RecipientType, string> = {
  CLIENT: 'Client (lead)',
  COUNSELOR: 'Counselor',
  ADMIN: 'Admins',
};

const BUILDER_STEP_TYPES: StepType[] = ['SEND_WHATSAPP', 'SEND_EMAIL', 'SEND_SMS', 'NOTIFY', 'WAIT'];

// Field options offered when mapping a WhatsApp template variable {{n}}.
const VAR_FIELD_OPTIONS: { value: string; label: string }[] = [
  { value: 'lead.name', label: 'Lead name' },
  { value: 'lead.firstName', label: 'Lead first name' },
  { value: 'lead.phone', label: 'Lead phone' },
  { value: 'lead.email', label: 'Lead email' },
  { value: 'lead.status', label: 'Lead status' },
  { value: 'counselor.name', label: 'Counselor name' },
  { value: 'counselor.email', label: 'Counselor email' },
  { value: 'counselor.phone', label: 'Counselor phone' },
];

function blankStep(): WorkflowStep {
  return { type: 'SEND_WHATSAPP', recipientType: 'CLIENT', delayMinutes: 0 };
}

// ── Builder drawer ─────────────────────────────────────────────────

function WorkflowBuilder({
  initial,
  waTemplates,
  emailTemplates,
  onClose,
  onSaved,
}: {
  initial: Workflow | null;
  waTemplates: TemplateOption[];
  emailTemplates: TemplateOption[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [triggerType, setTriggerType] = useState<TriggerType>(initial?.triggerType ?? 'LEAD_CREATED');
  const [statuses, setStatuses] = useState<string>(
    Array.isArray((initial?.triggerConfig as any)?.statuses)
      ? ((initial!.triggerConfig as any).statuses as string[]).join(', ')
      : '',
  );
  const [stopOnReply, setStopOnReply] = useState(initial?.stopOnReply ?? true);
  const [stopOnConvertOrLost, setStopOnConvertOrLost] = useState(initial?.stopOnConvertOrLost ?? true);
  const [quietStart, setQuietStart] = useState(initial?.quietHoursStart ?? '');
  const [quietEnd, setQuietEnd] = useState(initial?.quietHoursEnd ?? '');
  const [maxPerDay, setMaxPerDay] = useState<string>(initial?.maxPerLeadPerDay != null ? String(initial.maxPerLeadPerDay) : '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? false);
  const [steps, setSteps] = useState<WorkflowStep[]>(initial?.steps?.length ? initial.steps : [blankStep()]);
  const [saving, setSaving] = useState(false);

  const setStep = (i: number, patch: Partial<WorkflowStep>) =>
    setSteps((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const addStep = () => setSteps((prev) => [...prev, blankStep()]);
  const removeStep = (i: number) => setSteps((prev) => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!name.trim()) {
      toast('error', 'Give the workflow a name.');
      return;
    }
    setSaving(true);
    try {
      const triggerConfig =
        triggerType === 'LEAD_STATUS_CHANGED' && statuses.trim()
          ? { statuses: statuses.split(',').map((s) => s.trim()).filter(Boolean) }
          : {};
      const body = {
        name: name.trim(),
        triggerType,
        triggerConfig,
        isActive,
        stopOnReply,
        stopOnConvertOrLost,
        quietHoursStart: quietStart.trim() || null,
        quietHoursEnd: quietEnd.trim() || null,
        maxPerLeadPerDay: maxPerDay.trim() ? Math.max(1, Number(maxPerDay) || 0) || null : null,
        steps: steps.map((s) => ({
          type: s.type,
          recipientType: s.recipientType,
          whatsappTemplateId: s.type === 'SEND_WHATSAPP' ? s.whatsappTemplateId ?? null : null,
          emailTemplateId: s.type === 'SEND_EMAIL' ? s.emailTemplateId ?? null : null,
          delayMinutes: Number(s.delayMinutes) || 0,
          condition: s.condition ?? {},
          variableMapping: s.variableMapping ?? {},
        })),
      };
      if (initial) await updateWorkflow(initial.id, body);
      else await createWorkflow(body);
      toast('success', initial ? 'Workflow updated' : 'Workflow created');
      onSaved();
    } catch (e: any) {
      toast('error', e?.response?.data?.message ?? 'Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/30">
      <div className="flex h-full w-full max-w-lg flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h3 className="text-sm font-semibold text-slate-800">{initial ? 'Edit workflow' : 'New workflow'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          {/* Name */}
          <div>
            <label className="mb-1 block text-xs font-normal text-slate-600">Workflow name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
              placeholder="e.g. New lead follow-up"
            />
          </div>

          {/* Trigger */}
          <div>
            <label className="mb-1 block text-xs font-normal text-slate-600">When this happens (trigger)</label>
            <SelectField
              value={triggerType}
              onChange={(e) => setTriggerType(e.target.value as TriggerType)}
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
            >
              {(Object.keys(TRIGGER_LABELS) as TriggerType[]).map((t) => (
                <option key={t} value={t}>{TRIGGER_LABELS[t]}</option>
              ))}
            </SelectField>
            {triggerType === 'LEAD_STATUS_CHANGED' && (
              <input
                value={statuses}
                onChange={(e) => setStatuses(e.target.value)}
                className="mt-2 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                placeholder="Statuses (comma-separated), e.g. Converted, Lost — blank = any"
              />
            )}
          </div>

          {/* Steps */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-xs font-normal text-slate-600">Steps (run in order)</label>
              <button onClick={addStep} className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800">
                <Plus className="h-3.5 w-3.5" /> Add step
              </button>
            </div>
            <div className="space-y-3">
              {steps.map((s, i) => {
                const Icon = STEP_META[s.type].icon;
                return (
                  <div key={i} className="rounded-lg border border-gray-200 bg-slate-50 p-3">
                    <div className="mb-2 flex items-center gap-2">
                      <GripVertical className="h-4 w-4 text-slate-300" />
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-indigo-100 text-[11px] font-semibold text-indigo-600">{i + 1}</span>
                      <Icon className="h-4 w-4 text-slate-500" />
                      <SelectField
                        value={s.type}
                        onChange={(e) => setStep(i, { type: e.target.value as StepType })}
                        className="flex-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs"
                      >
                        {BUILDER_STEP_TYPES.map((t) => (
                          <option key={t} value={t}>{STEP_META[t].label}</option>
                        ))}
                      </SelectField>
                      <button onClick={() => removeStep(i)} className="text-slate-400 hover:text-red-600" disabled={steps.length === 1}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      {/* Recipient (not for WAIT) */}
                      {s.type !== 'WAIT' && (
                        <div>
                          <label className="mb-0.5 block text-[10px] text-slate-400">Send to</label>
                          <SelectField
                            value={s.recipientType}
                            onChange={(e) => setStep(i, { recipientType: e.target.value as RecipientType })}
                            className="w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-xs"
                          >
                            {(Object.keys(RECIPIENT_LABELS) as RecipientType[])
                              .filter((r) => !(s.type === 'NOTIFY' && r === 'CLIENT'))
                              .map((r) => (
                                <option key={r} value={r}>{RECIPIENT_LABELS[r]}</option>
                              ))}
                          </SelectField>
                        </div>
                      )}

                      {/* Delay */}
                      <div>
                        <label className="mb-0.5 block text-[10px] text-slate-400">Wait before (minutes)</label>
                        <input
                          type="number"
                          min={0}
                          value={s.delayMinutes ?? 0}
                          onChange={(e) => setStep(i, { delayMinutes: Math.max(0, Number(e.target.value) || 0) })}
                          className="w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-xs"
                        />
                      </div>

                      {/* Template picker */}
                      {s.type === 'SEND_WHATSAPP' && (
                        <div className="col-span-2">
                          <label className="mb-0.5 block text-[10px] text-slate-400">WhatsApp template (approved)</label>
                          <SelectField
                            value={s.whatsappTemplateId ?? ''}
                            onChange={(e) => setStep(i, { whatsappTemplateId: e.target.value || null })}
                            className="w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-xs"
                          >
                            <option value="">— Select a template —</option>
                            {waTemplates.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                          </SelectField>
                          {waTemplates.length === 0 && <p className="mt-0.5 text-[10px] text-amber-500">No approved templates — create one first.</p>}

                          {/* Variable mapping — one row per {{n}} in the template body */}
                          {(() => {
                            const opt = waTemplates.find((t) => t.id === s.whatsappTemplateId);
                            const n = opt?.vars ?? 0;
                            if (!opt || n === 0) return null;
                            return (
                              <div className="mt-2 space-y-1.5 rounded-md bg-white p-2">
                                <p className="text-[10px] font-medium text-slate-500">Fill template variables</p>
                                {Array.from({ length: n }).map((_, k) => {
                                  const key = String(k + 1);
                                  const mapping = (s.variableMapping ?? {}) as Record<string, string>;
                                  return (
                                    <div key={key} className="flex items-center gap-2">
                                      <span className="w-8 flex-shrink-0 font-mono text-[10px] text-slate-400">{`{{${key}}}`}</span>
                                      <SelectField
                                        value={mapping[key] ?? 'lead.name'}
                                        onChange={(e) => setStep(i, { variableMapping: { ...mapping, [key]: e.target.value } })}
                                        className="flex-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs"
                                      >
                                        {VAR_FIELD_OPTIONS.map((f) => (
                                          <option key={f.value} value={f.value}>{f.label}</option>
                                        ))}
                                      </SelectField>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      {s.type === 'SEND_EMAIL' && (
                        <div className="col-span-2">
                          <label className="mb-0.5 block text-[10px] text-slate-400">Email template</label>
                          <SelectField
                            value={s.emailTemplateId ?? ''}
                            onChange={(e) => setStep(i, { emailTemplateId: e.target.value || null })}
                            className="w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-xs"
                          >
                            <option value="">— Select a template —</option>
                            {emailTemplates.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                          </SelectField>
                        </div>
                      )}
                      {s.type === 'NOTIFY' && (
                        <div className="col-span-2">
                          <label className="mb-0.5 block text-[10px] text-slate-400">Notification text (optional)</label>
                          <input
                            value={(s.condition as any)?.notifyBody ?? ''}
                            onChange={(e) => setStep(i, { condition: { ...(s.condition ?? {}), notifyBody: e.target.value } })}
                            className="w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-xs"
                            placeholder="e.g. New lead {{lead.name}} assigned to you"
                          />
                        </div>
                      )}
                      {s.type === 'SEND_SMS' && (
                        <div className="col-span-2">
                          <label className="mb-0.5 block text-[10px] text-slate-400">SMS message</label>
                          <textarea
                            rows={2}
                            value={(s.condition as any)?.smsBody ?? ''}
                            onChange={(e) => setStep(i, { condition: { ...(s.condition ?? {}), smsBody: e.target.value } })}
                            className="w-full resize-none rounded-md border border-gray-200 bg-white px-2 py-1 text-xs"
                            placeholder="e.g. Hi {{lead.name}}, thanks for your interest! Our counselor will call you shortly."
                          />
                          <p className="mt-0.5 text-[10px] text-slate-400">Use {'{{lead.name}}'}, {'{{counselor.name}}'} etc. India: SMS needs a DLT-approved template on your provider.</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Guardrails */}
          <div className="rounded-lg border border-gray-100 bg-white p-3">
            <p className="mb-2 text-xs font-medium text-slate-600">Stop rules</p>
            <label className="mb-1.5 flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={stopOnReply} onChange={(e) => setStopOnReply(e.target.checked)} />
              Stop if the client replies
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={stopOnConvertOrLost} onChange={(e) => setStopOnConvertOrLost(e.target.checked)} />
              Stop if the lead is converted or lost
            </label>

            <div className="mt-3 border-t border-gray-100 pt-3">
              <p className="mb-1.5 text-xs font-medium text-slate-600">
                Quiet hours <span className="font-normal text-slate-400">(client messages paused; counselor alerts still send)</span>
              </p>
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <span>From</span>
                <TimeDropdown className="w-28" placeholder="Start" value={quietStart} onChange={setQuietStart} />
                <span>to</span>
                <TimeDropdown className="w-28" placeholder="End" value={quietEnd} onChange={setQuietEnd} />
                {(quietStart || quietEnd) && (
                  <button onClick={() => { setQuietStart(''); setQuietEnd(''); }} className="text-slate-400 hover:text-slate-600">clear</button>
                )}
              </div>

              <div className="mt-3 flex items-center gap-2 text-xs text-slate-600">
                <span>Max messages per lead per day</span>
                <input
                  type="number"
                  min={1}
                  value={maxPerDay}
                  onChange={(e) => setMaxPerDay(e.target.value)}
                  placeholder="∞"
                  className="w-20 rounded-md border border-gray-200 px-2 py-1 text-xs"
                />
                <span className="text-slate-400">(blank = no cap)</span>
              </div>
            </div>
          </div>

          {/* Active */}
          <label className="flex items-center gap-2 text-sm font-normal text-slate-700">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            Active (turn the automation on)
          </label>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-3">
          <button onClick={onClose} className="rounded-md border border-gray-200 px-4 py-2 text-sm text-slate-600">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-slate-300"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {initial ? 'Save changes' : 'Create workflow'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Activity drawer ────────────────────────────────────────────────

const RUN_STATUS_BADGE: Record<string, string> = {
  ACTIVE: 'bg-indigo-100 text-indigo-700',
  COMPLETED: 'bg-emerald-100 text-emerald-700',
  STOPPED: 'bg-amber-100 text-amber-700',
  FAILED: 'bg-red-100 text-red-700',
};
const STEP_STATUS_COLOR: Record<string, string> = {
  SENT: 'text-emerald-600',
  SKIPPED: 'text-slate-400',
  FAILED: 'text-red-600',
};

function ActivityDrawer({ workflow, onClose }: { workflow: Workflow; onClose: () => void }) {
  const [runs, setRuns] = useState<WorkflowRunRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getWorkflowRuns(workflow.id)
      .then(setRuns)
      .catch(() => setRuns([]))
      .finally(() => setLoading(false));
  }, [workflow.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/30">
      <div className="flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Activity</h3>
            <p className="text-xs text-slate-400">{workflow.name}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-indigo-400" /></div>
          ) : runs.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-400">No runs yet — enroll a lead to see activity.</p>
          ) : (
            runs.map((r) => (
              <div key={r.id} className="rounded-lg border border-gray-100 p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate text-sm font-medium text-slate-700">{r.leadName || r.leadId}</p>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${RUN_STATUS_BADGE[r.status] ?? 'bg-slate-100 text-slate-500'}`}>
                    {r.status}{r.stoppedReason ? ` · ${r.stoppedReason}` : ''}
                  </span>
                </div>
                {r.steps.length > 0 ? (
                  <ul className="mt-2 space-y-1">
                    {r.steps.map((s, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-[11px]">
                        <span className="font-mono text-slate-400">#{s.stepOrder}</span>
                        <span className="text-slate-500">{s.stepType.replace('SEND_', '').toLowerCase()} → {s.recipientType.toLowerCase()}</span>
                        <span className={`ml-auto font-medium ${STEP_STATUS_COLOR[s.status] ?? 'text-slate-400'}`}>
                          {s.status}{s.info && s.status !== 'SENT' ? ` (${s.info})` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-1 text-[11px] text-slate-400">Waiting for first step…</p>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────

export default function WorkflowsPage() {
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editing, setEditing] = useState<Workflow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Workflow | null>(null);
  const [activityFor, setActivityFor] = useState<Workflow | null>(null);
  const [waTemplates, setWaTemplates] = useState<TemplateOption[]>([]);
  const [emailTemplates, setEmailTemplates] = useState<TemplateOption[]>([]);

  const load = useCallback(() => {
    setLoading(true);
    listWorkflows()
      .then(setWorkflows)
      .catch(() => toast('error', 'Failed to load workflows'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    listWhatsappTemplateOptions().then(setWaTemplates);
    listEmailTemplateOptions().then(setEmailTemplates);
  }, [load]);

  const openNew = () => { setEditing(null); setBuilderOpen(true); };
  const openEdit = (wf: Workflow) => { setEditing(wf); setBuilderOpen(true); };

  const onToggle = async (wf: Workflow) => {
    try {
      await toggleWorkflow(wf.id, !wf.isActive);
      setWorkflows((prev) => prev.map((w) => (w.id === wf.id ? { ...w, isActive: !w.isActive } : w)));
    } catch {
      toast('error', 'Failed to toggle');
    }
  };

  const onDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteWorkflow(deleteTarget.id);
      setWorkflows((prev) => prev.filter((w) => w.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch {
      toast('error', 'Failed to delete');
    }
  };

  return (
    <DashboardLayout title="Workflows">
      <div className="mx-auto max-w-3xl space-y-5 px-4 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-800">Workflow Automations</h1>
            <p className="mt-0.5 text-sm text-slate-500">Multi-step WhatsApp + Email follow-up — for clients and counselors.</p>
          </div>
          <button onClick={openNew} className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700">
            <Plus className="h-4 w-4" /> New workflow
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
        ) : workflows.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 py-16 text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-indigo-50"><Zap className="h-7 w-7 text-indigo-500" /></div>
            <p className="text-sm font-medium text-slate-700">No workflows yet</p>
            <p className="mt-1 text-xs text-slate-400">Create one to auto-follow-up with leads and alert counselors.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {workflows.map((wf) => (
              <div key={wf.id} className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-slate-800">{wf.name}</p>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${wf.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {wf.isActive ? 'Active' : 'Off'}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {TRIGGER_LABELS[wf.triggerType]} · {stepCount(wf)} step{stepCount(wf) === 1 ? '' : 's'}
                      {wf._count ? ` · ${wf._count.runs} run${wf._count.runs === 1 ? '' : 's'}` : ''}
                      {wf.stats && wf.stats.SENT > 0 ? ` · ${wf.stats.SENT} sent` : ''}
                      {wf.stats && wf.stats.FAILED > 0 ? (
                        <span className="text-red-500"> · {wf.stats.FAILED} failed</span>
                      ) : null}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => onToggle(wf)} className={`relative h-6 w-11 rounded-full transition-colors ${wf.isActive ? 'bg-indigo-500' : 'bg-slate-200'}`} title="Toggle active">
                      <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${wf.isActive ? 'translate-x-5' : ''}`} />
                    </button>
                    <button onClick={() => setActivityFor(wf)} className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-50" title="Activity"><Activity className="h-3.5 w-3.5" /></button>
                    <button onClick={() => openEdit(wf)} className="rounded-md border border-gray-200 px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-50">Edit</button>
                    <button onClick={() => setDeleteTarget(wf)} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {builderOpen && (
        <WorkflowCanvasBuilder
          initial={editing}
          waTemplates={waTemplates}
          emailTemplates={emailTemplates}
          onClose={() => setBuilderOpen(false)}
          onSaved={() => { setBuilderOpen(false); load(); }}
        />
      )}

      {activityFor && (
        <ActivityDrawer workflow={activityFor} onClose={() => setActivityFor(null)} />
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete workflow"
          message={`Delete "${deleteTarget.name}"? Active runs for this workflow will stop.`}
          confirmText="Delete"
          isOpen={true}
          onConfirm={onDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </DashboardLayout>
  );
}
